import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Popover, ToggleButton, ToggleButtonGroup } from '@heroui/react';
import { SettingsIcon } from '@/components/icons';
import { Switch } from '@/components/ui/Switch';
import { Tooltip } from '@/components/Tooltip';
import { voiceMedia } from '@/lib/voiceMedia';
import type { ScreenFps, ScreenResolution } from '@/lib/voicePreferences';

/** Tiers offered mid-share. 4k is deliberately absent — the desktop picker
 *  doesn't offer it either, and stepping INTO 4k mid-share is never what a
 *  struggling uplink needs. `source` = native resolution. */
const RESOLUTIONS: readonly ScreenResolution[] = ['720p', '1080p', '1440p', 'source'];
const FPS_OPTIONS: readonly ScreenFps[] = [15, 30, 60];

/**
 * Live screen-share quality controls — shown in the voice-room control dock
 * while the local user is sharing. Lets the sharer drop from 2K to Full HD
 * (or lower) WITHOUT restarting the share: applyConstraints renegotiates the
 * capture and the encoder bitrate is retargeted in place. The «Авто» switch
 * enables adaptive stepping down/up driven by the sender's bandwidth/CPU
 * limitation stats (see screenShareAutoQuality).
 */
export function ScreenShareQualityMenu({ onOpenChange }: { onOpenChange?: (open: boolean) => void }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [quality, setQuality] = useState(() => voiceMedia.getScreenShareQuality());

  // While the menu is open, poll the applied step so the "currently sending"
  // line follows the auto controller's moves (it samples every 3s).
  useEffect(() => {
    if (!open) return;
    setQuality(voiceMedia.getScreenShareQuality());
    const timer = setInterval(() => setQuality(voiceMedia.getScreenShareQuality()), 2000);
    return () => clearInterval(timer);
  }, [open]);

  const pick = (patch: Partial<{ resolution: ScreenResolution; fps: ScreenFps }>) => {
    void voiceMedia.setScreenShareQuality({
      resolution: patch.resolution ?? quality.resolution,
      fps: patch.fps ?? quality.fps,
    });
    setQuality(voiceMedia.getScreenShareQuality());
  };

  const applied = quality.applied;
  const appliedDiffers =
    applied && (applied.resolution !== quality.resolution || applied.fps !== quality.fps);

  return (
    <Popover isOpen={open} onOpenChange={(nextOpen) => {
      setOpen(nextOpen);
      onOpenChange?.(nextOpen);
    }}>
      <Tooltip label={t('voice.screenQualityTitle')}>
        <Popover.Trigger
          aria-label={t('voice.screenQualityTitle')}
          // Named group: the whole voice room is a `group` (it reveals this dock on
          // hover), so a bare group-hover would spin the gear on any room hover.
          className="group/quality-gear flex h-[30px] w-[30px] items-center justify-center rounded-full bg-surface-secondary text-ink transition-colors hover:bg-default"
        >
          <SettingsIcon className="h-4 w-4 motion-safe:transition-transform motion-safe:duration-700 motion-safe:ease-out motion-safe:group-hover/quality-gear:rotate-[360deg]" />
        </Popover.Trigger>
      </Tooltip>
      <Popover.Content className="w-[calc(100vw-2rem)] max-w-[30rem]" placement="top">
        <Popover.Dialog className="p-3">
          <div className="mb-2 text-xs font-medium text-ink">{t('voice.screenQualityTitle')}</div>
          <div className="font-medium mb-1 text-[10px] text-ink-muted">
            {t('prof.voice.resolution')}
          </div>
          <ToggleButtonGroup
            fullWidth
            selectionMode="single"
            disallowEmptySelection
            aria-label={t('prof.voice.resolution')}
            selectedKeys={[quality.resolution]}
            onSelectionChange={(keys) => {
              const v = [...keys][0] as ScreenResolution;
              pick({ resolution: v });
            }}
          >
            {RESOLUTIONS.map((r) => (
              <ToggleButton key={r} id={r}>
                {r === 'source' ? t('prof.voice.resolutionNative') : r}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
          <div className="font-medium mb-1 mt-2 text-[10px] text-ink-muted">
            {t('prof.voice.fps')}
          </div>
          <ToggleButtonGroup
            fullWidth
            selectionMode="single"
            disallowEmptySelection
            aria-label={t('prof.voice.fps')}
            selectedKeys={[String(quality.fps)]}
            onSelectionChange={(keys) => {
              const v = Number([...keys][0]) as ScreenFps;
              if ([15, 30, 60].includes(v)) pick({ fps: v });
            }}
          >
            {FPS_OPTIONS.map((fps) => (
              <ToggleButton key={fps} id={String(fps)}>
                {fps} fps
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
          <label className="mt-3 flex items-center gap-2 text-xs text-ink-dim">
            <Switch
              checked={quality.auto}
              onChange={(v) => {
                voiceMedia.setScreenShareAutoQuality(v);
                setQuality(voiceMedia.getScreenShareQuality());
              }}
            />
            <span>{t('voice.screenQualityAuto')}</span>
          </label>
          {appliedDiffers && applied && (
            <p className="mt-2 text-[11px] leading-snug text-ink-muted">
              {t('voice.screenQualityApplied', {
                quality: `${applied.resolution === 'source' ? t('prof.voice.resolutionNative') : applied.resolution} @ ${applied.fps}`,
              })}
            </p>
          )}

          {/* Sound status. Every «друг не слышит демку» report was impossible to
              tell apart from a working share, because nothing said whether sound
              was actually leaving this machine. Now the sharer can see it.
              Contrast matters here: the first cut used text-ink-muted on the
              popover surface and was reported as unreadable — a status nobody
              can read is no better than no status, so failures get a tinted
              danger row and the rest use the normal body ink. */}
          <div
            className={`mt-2.5 flex items-start gap-2 rounded-lg px-2 py-1.5 text-[11px] leading-snug ${
              quality.audio === 'failed'
                ? 'bg-danger/12 text-danger-ink'
                : quality.audio === 'sending'
                  ? 'bg-success/10 text-ink'
                  : 'bg-default text-ink-dim'
            }`}
          >
            <span
              aria-hidden="true"
              className={`mt-[5px] h-1.5 w-1.5 shrink-0 rounded-full ${
                quality.audio === 'sending'
                  ? 'bg-success'
                  : quality.audio === 'failed'
                    ? 'bg-danger'
                    : 'bg-ink-dim'
              }`}
            />
            <span>{t(`voice.screenAudioStatus.${quality.audio}`)}</span>
          </div>
        </Popover.Dialog>
      </Popover.Content>
    </Popover>
  );
}
